<template>
  <div class="floor-plan-container">
    <img
      ref="floorPlanImage"
      :src="floorPlan.image_url"
      :alt="floorPlan.name"
      class="floor-plan-image"
      crossorigin="anonymous"
      @load="onImageLoad"
    />

    <!-- 全トラップ未配置時のヒントメッセージ -->
    <div v-if="hasNoPlacedTraps" class="placement-hint-overlay">
      <v-card class="placement-hint-card" elevation="3">
        <v-card-text class="text-center">
          <v-icon large class="mb-2">mdi-information-outline</v-icon>
          <div class="text-h6 mb-2">{{ $t('placement_hint.title') }}</div>
          <div class="text-body-2">{{ $t('placement_hint.description') }}</div>
        </v-card-text>
      </v-card>
    </div>

    <canvas
      v-show="isHeatmapMode"
      ref="heatmapCanvas"
      class="heatmap-overlay"
      data-testid="heatmap-canvas"
    />

    <svg
      ref="svgOverlay"
      class="overlay"
      role="img"
      :aria-label="$t('floor_plan_canvas')"
      :viewBox="viewBox"
      preserveAspectRatio="xMinYMin meet"
      @dragover.prevent
      @drop="handleDrop"
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp"
    >
      <!-- トラップグループ（円とマーカー） -->
      <g
        v-for="trap in placedTraps"
        :key="trap.trapNo"
        class="trap-group"
        :data-testid="`trap-group-${trap.trapNo}`"
        @mouseenter="handleTrapMouseEnter($event, trap)"
        @mouseleave="handleTrapMouseLeave"
      >
        <!-- 捕獲数表示: 単一円（ヒートマップモード・配置のみモード時は非表示） -->
        <circle
          v-if="trap.count > 0 && isCircleVisible"
          :cx="trap.coordinates.x"
          :cy="trap.coordinates.y"
          :r="calculateCircleRadius(trap.count)"
          fill="rgba(204, 0, 0, 0.5)"
          :aria-label="$t('trap_label', { trapNo: trap.trapNo })"
          class="trap-circle trap-circle--current"
          @mousedown="handleMouseDown($event, trap.trapNo)"
        />
        <!--
          ヒートマップモード時のドラッグ用当たり判定。
          マーカー（正方形）より広い当たり判定領域を確保するため、半径をmarkerSizeの1.5倍にしている。
          ヒートマップ描画でcountの円が非表示になるため、この透明円がないとドラッグ開始が困難になる。
        -->
        <circle
          v-if="isHeatmapMode"
          :cx="trap.coordinates.x"
          :cy="trap.coordinates.y"
          :r="markerSize * 1.5"
          fill="transparent"
          class="trap-circle"
          @mousedown="handleMouseDown($event, trap.trapNo)"
        />

        <!-- トラップマーカー -->
        <rect
          :x="trap.coordinates.x - markerSize / 2"
          :y="trap.coordinates.y - markerSize / 2"
          :width="markerSize"
          :height="markerSize"
          fill="#FFD700"
          stroke="black"
          :stroke-width="strokeWidth"
          :class="['trap-marker', { 'trap-marker--heatmap': isHeatmapMode }]"
          :aria-label="$t('trap_marker', { trapNo: trap.trapNo })"
          @mousedown="handleMouseDown($event, trap.trapNo)"
        />

        <!--
          トラップラベル（白縁付き）
          注: html-to-imageはCSSを正しく処理できないため、見た目に関わる属性は
          すべてインラインで指定する（font-family, font-weight, paint-order等）
        -->
        <text
          v-if="labelDisplayMode !== DISPLAY_MODES.NONE"
          :x="getLabelX(trap)"
          :y="trap.coordinates.y"
          class="trap-label"
        >
          <!-- トラップ名のみ: 1行表示 -->
          <template v-if="labelDisplayMode === DISPLAY_MODES.NAME">
            <tspan
              :font-size="18 * sizeScale"
              :stroke-width="3 * sizeScale"
              stroke="white"
              fill="#333"
              paint-order="stroke fill"
              dominant-baseline="middle"
              class="trap-label-name"
            >
              {{ getTrapName(trap) }}
            </tspan>
          </template>
          <!-- トラップ名+捕獲数: 2段表示 -->
          <template v-else-if="labelDisplayMode === DISPLAY_MODES.NAME_AND_COUNT">
            <tspan
              :x="getLabelX(trap)"
              :dy="-6 * sizeScale"
              :font-size="18 * sizeScale"
              :stroke-width="3 * sizeScale"
              stroke="white"
              fill="#333"
              paint-order="stroke fill"
              class="trap-label-name"
            >
              {{ getTrapName(trap) }}
            </tspan>
            <tspan
              :x="getLabelX(trap)"
              :dy="14 * sizeScale"
              :font-size="14 * sizeScale"
              :stroke-width="4 * sizeScale"
              stroke="white"
              font-weight="500"
              fill="#444"
              paint-order="stroke fill"
              class="trap-label-count"
            >
              ({{ getCountDisplay(trap) }})
            </tspan>
          </template>
        </text>
      </g>

      <!-- ヒートマップ凡例 -->
      <g v-if="isHeatmapMode" class="heatmap-legend" data-testid="heatmap-legend">
        <defs>
          <linearGradient id="heatGradient">
            <stop offset="0%" stop-color="rgb(0,0,255)" />
            <stop offset="25%" stop-color="rgb(0,255,0)" />
            <stop offset="50%" stop-color="rgb(255,255,0)" />
            <stop offset="100%" stop-color="rgb(255,0,0)" />
          </linearGradient>
          <linearGradient id="comparisonGradient">
            <stop offset="0%" stop-color="rgb(49,54,149)" />
            <stop offset="25%" stop-color="rgb(145,191,219)" />
            <stop offset="50%" stop-color="rgb(255,255,255)" />
            <stop offset="75%" stop-color="rgb(223,136,104)" />
            <stop offset="100%" stop-color="rgb(180,4,38)" />
          </linearGradient>
        </defs>
        <!-- 背景 -->
        <rect
          :x="legendX - 8 * sizeScale"
          :y="legendY - 4 * sizeScale"
          :width="legendBarWidth + 16 * sizeScale"
          :height="legendBarHeight + 32 * sizeScale"
          :rx="4 * sizeScale"
          fill="rgba(255,255,255,0.85)"
          stroke="#ccc"
          :stroke-width="sizeScale"
        />
        <!-- カラーバー -->
        <rect
          :x="legendX"
          :y="legendY"
          :width="legendBarWidth"
          :height="legendBarHeight"
          :fill="displayMode === DISPLAY_MODES.COMPARISON_HEAT ? 'url(#comparisonGradient)' : 'url(#heatGradient)'"
          stroke="#999"
          :stroke-width="sizeScale * 0.5"
        />
        <!-- ラベル: 通常ヒートマップ -->
        <template v-if="displayMode === DISPLAY_MODES.HEAT">
          <text
            :x="legendX"
            :y="legendY + legendBarHeight + 14 * sizeScale"
            :font-size="14 * sizeScale"
            :stroke-width="3 * sizeScale"
            stroke="white"
            fill="#333"
            font-family="sans-serif"
            font-weight="bold"
            paint-order="stroke fill"
            text-anchor="start"
          >{{ $t('legend.low') }}</text>
          <text
            :x="legendX + legendBarWidth"
            :y="legendY + legendBarHeight + 14 * sizeScale"
            :font-size="14 * sizeScale"
            :stroke-width="3 * sizeScale"
            stroke="white"
            fill="#333"
            font-family="sans-serif"
            font-weight="bold"
            paint-order="stroke fill"
            text-anchor="end"
          >{{ $t('legend.high') }}</text>
        </template>
        <!-- ラベル: 比較ヒートマップ -->
        <template v-else>
          <text
            :x="legendX"
            :y="legendY + legendBarHeight + 14 * sizeScale"
            :font-size="14 * sizeScale"
            :stroke-width="3 * sizeScale"
            stroke="white"
            fill="#333"
            font-family="sans-serif"
            font-weight="bold"
            paint-order="stroke fill"
            text-anchor="start"
          >{{ $t('legend.decrease') }}</text>
          <text
            :x="legendX + legendBarWidth / 2"
            :y="legendY + legendBarHeight + 14 * sizeScale"
            :font-size="14 * sizeScale"
            :stroke-width="3 * sizeScale"
            stroke="white"
            fill="#333"
            font-family="sans-serif"
            font-weight="bold"
            paint-order="stroke fill"
            text-anchor="middle"
          >{{ $t('legend.no_change') }}</text>
          <text
            :x="legendX + legendBarWidth"
            :y="legendY + legendBarHeight + 14 * sizeScale"
            :font-size="14 * sizeScale"
            :stroke-width="3 * sizeScale"
            stroke="white"
            fill="#333"
            font-family="sans-serif"
            font-weight="bold"
            paint-order="stroke fill"
            text-anchor="end"
          >{{ $t('legend.increase') }}</text>
        </template>
      </g>

      <!--
        情報テキスト（白縁付き・エクスポート専用）
        visibility: hiddenで通常非表示、html-to-imageがCSSを無視するためエクスポート時のみ表示される
      -->
      <g v-if="exportInfoLines.length" class="export-info" data-testid="export-info">
        <text
          v-for="(line, index) in exportInfoLines"
          :key="index"
          :x="10 * sizeScale"
          :y="(20 + index * 20) * sizeScale"
          :font-size="14 * sizeScale"
          :stroke-width="3 * sizeScale"
          stroke="white"
          fill="#333"
          font-family="sans-serif"
          font-weight="bold"
          paint-order="stroke fill"
          class="export-info-text"
        >
          {{ line }}
        </text>
      </g>
    </svg>

    <!-- パイチャートポップアップ -->
    <PieChartPopup
      v-model="showPopup"
      :trap="hoveredTrap"
      :position-x="popupPosition.x"
      :position-y="popupPosition.y"
    />
  </div>
</template>

<i18n lang="yaml">
ja:
  insect_count: 捕獲数
  floor_plan_canvas: 図面キャンバス
  trap_label: 'トラップ {trapNo}'
  trap_marker: 'トラップ {trapNo} マーカー'
  placement_hint:
    title: トラップを配置しましょう
    description: |
      右側のトラップ一覧からトラップをドラッグして
      図面上にドロップしてください
  legend:
    low: 少
    high: 多
    decrease: '−'
    no_change: '0'
    increase: '+'
  export_info:
    place: 設置場所名
    floor_plan: 図面
    period: 期間
    insect_filter: 虫種

en:
  insect_count: Count
  floor_plan_canvas: Floor Plan Canvas
  trap_label: 'Trap {trapNo}'
  trap_marker: 'Trap {trapNo} marker'
  placement_hint:
    title: Place Traps on the Floor Plan
    description: |
      Drag traps from the list on the right
      and drop them onto the floor plan
  legend:
    low: Low
    high: High
    decrease: '−'
    no_change: '0'
    increase: '+'
  export_info:
    place: Place
    floor_plan: Floor Plan
    period: Period
    insect_filter: Insect Filter
</i18n>

<script>
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import { screenToSvgCoordinates } from '@/libs/floorPlanCoordinates';
import { throttle } from 'lodash';
import { DISPLAY_MODES } from './displayModes';
import { HeatmapRenderer } from './heatmapRenderer';
import PieChartPopup from './PieChartPopup';

const DEFAULT_VIEWBOX = '0 0 1000 1000';
const HOVER_DELAY_MS = 750;

// トラップマーカーのサイズ設定
// 基準画像サイズ（短辺1000px）での実際のピクセル値
const BASE_IMAGE_SIZE = 1000;          // 基準となる画像サイズ（短辺）
const MARKER_SIZE_AT_BASE = 16;        // 基準サイズでのマーカーの四角形サイズ
const STROKE_WIDTH_AT_BASE = 2;        // 基準サイズでのマーカー枠線幅
const MIN_CIRCLE_RADIUS_AT_BASE = 14;  // 基準サイズでの最小円半径（捕獲数0）
const MAX_CIRCLE_RADIUS_AT_BASE = 100; // 基準サイズでの最大円半径
const FALLBACK_CIRCLE_SCALE = 1000;     // イベント不足時のフォールバック値

export default {
  name: 'FloorPlanCanvas',
  components: {
    PieChartPopup,
  },
  inject: {
    createHeatmapRenderer: {
      default() {
        return (canvas) => new HeatmapRenderer(canvas);
      },
    },
  },
  props: {
    floorPlan: {
      type: Object,
      required: true,
    },
    traps: {
      type: Array,
      default: () => [],
    },
    displayMode: {
      type: String,
      default: DISPLAY_MODES.NONE,
      validator: (value) => Object.values(DISPLAY_MODES).includes(value),
    },
    placeName: {
      type: String,
      default: '',
    },
    selectedMonth: {
      type: String,
      default: '',
    },
    insectFilterLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      viewBox: DEFAULT_VIEWBOX,
      draggingTrap: null,
      markerSize: MARKER_SIZE_AT_BASE,
      // 画像の実サイズ情報を保存
      imageDimensions: {
        width: 0,
        height: 0,
        minDimension: 0,
      },
      // ホバーポップアップ用
      hoveredTrap: null,
      hoverTimer: null,
      popupPosition: { x: 0, y: 0 },
      heatmapRenderer: null,
    };
  },
  computed: {
    hasNoPlacedTraps() {
      return !this.traps.some(trap => trap.isPlaced);
    },
    placedTraps() {
      return this.traps.filter(trap => trap.isPlaced);
    },
    // 実際の画像サイズに対するスケール比率
    sizeScale() {
      return this.imageDimensions.minDimension > 0
        ? this.imageDimensions.minDimension / BASE_IMAGE_SIZE
        : 1.0;
    },
    // 画像サイズに応じた半径を算出する計算プロパティ
    minCircleRadius() {
      return MIN_CIRCLE_RADIUS_AT_BASE * this.sizeScale;
    },
    maxCircleRadius() {
      return MAX_CIRCLE_RADIUS_AT_BASE * this.sizeScale;
    },
    strokeWidth() {
      return STROKE_WIDTH_AT_BASE * this.sizeScale;
    },
    showPopup: {
      get() {
        return this.hoveredTrap !== null;
      },
      set(value) {
        if (!value) {
          this.hoveredTrap = null;
        }
      },
    },
    exportInfoLines() {
      // propsがすべて揃っていない場合は空配列を返す
      if (!this.placeName || !this.floorPlan?.name || !this.selectedMonth || !this.insectFilterLabel) {
        return [];
      }
      return [
        `${this.$t('export_info.place')}: ${this.placeName}`,
        `${this.$t('export_info.floor_plan')}: ${this.floorPlan.name}`,
        `${this.$t('export_info.period')}: ${this.selectedMonth}`,
        `${this.$t('export_info.insect_filter')}: ${this.insectFilterLabel}`,
      ];
    },
    // 円のスケーリングに使用する上限値（APIから取得、フォールバック対応）
    circleScaleMax() {
      return this.floorPlan.circle_scale_max ?? FALLBACK_CIRCLE_SCALE;
    },
    isHeatmapMode() {
      return this.displayMode === DISPLAY_MODES.HEAT || this.displayMode === DISPLAY_MODES.COMPARISON_HEAT;
    },
    isPlacementOnly() {
      return this.displayMode === DISPLAY_MODES.PLACEMENT_ONLY;
    },
    isCircleVisible() {
      return !this.isPlacementOnly && !this.isHeatmapMode;
    },
    heatmapRadius() {
      return MAX_CIRCLE_RADIUS_AT_BASE * this.sizeScale;
    },
    // ラベル表示判定（ヒートマップモード・配置のみモード時は常にnone扱い）
    labelDisplayMode() {
      return (this.isHeatmapMode || this.isPlacementOnly) ? DISPLAY_MODES.NONE : this.displayMode;
    },
    // ヒートマップ凡例の配置・サイズ
    legendBarWidth() {
      return 120 * this.sizeScale;
    },
    legendBarHeight() {
      return 12 * this.sizeScale;
    },
    legendX() {
      return this.imageDimensions.width - this.legendBarWidth - 30 * this.sizeScale;
    },
    legendY() {
      return 15 * this.sizeScale;
    },
  },
  watch: {
    traps: 'renderHeatmap',
    displayMode: 'renderHeatmap',
  },
  created() {
    this.DISPLAY_MODES = DISPLAY_MODES;
    this.throttledRenderHeatmap = throttle(this.renderHeatmapImmediate, 150);
  },
  beforeDestroy() {
    this.clearHoverTimer();
    if (this.throttledRenderHeatmap.cancel) {
      this.throttledRenderHeatmap.cancel();
    }
  },
  methods: {
    async exportImage() {
      // html-to-imageでPNG化
      const dataUrl = await toPng(this.$el, {
        cacheBust: false, // S3署名付きURLにタイムスタンプを付与すると署名が無効化されるため無効
        skipFonts: true, // 外部Webフォント埋め込みをスキップ（CORS対策）
      });

      // ダウンロード
      const blob = await fetch(dataUrl).then(res => res.blob());
      const fileName = `${this.floorPlan.name}_${this.selectedMonth}.png`;
      saveAs(blob, fileName);
    },
    onImageLoad() {
      const img = this.$refs.floorPlanImage;
      const { naturalWidth, naturalHeight } = img;
      const minDimension = Math.min(naturalWidth, naturalHeight);

      // 画像サイズ情報を保存
      this.imageDimensions = {
        width: naturalWidth,
        height: naturalHeight,
        minDimension,
      };

      // viewBoxを設定
      this.viewBox = `0 0 ${naturalWidth} ${naturalHeight}`;

      // マーカーサイズを画像サイズに基づいて計算
      this.markerSize = MARKER_SIZE_AT_BASE * this.sizeScale;

      // ヒートマップCanvasのサイズを画像に合わせる
      if (!this.heatmapRenderer) {
        this.heatmapRenderer = this.createHeatmapRenderer(this.$refs.heatmapCanvas);
      }
      this.heatmapRenderer.resize(naturalWidth, naturalHeight);

      // ヒートマップ描画（モードが有効な場合）
      this.renderHeatmap();
    },
    renderHeatmap() {
      if (!this.isHeatmapMode) {
        if (this.heatmapRenderer) {
          this.heatmapRenderer.clear();
        }
        return;
      }
      this.throttledRenderHeatmap();
    },
    renderHeatmapImmediate() {
      if (!this.heatmapRenderer) return;

      const options = { radius: this.heatmapRadius, circleScaleMax: this.circleScaleMax };
      this.heatmapRenderer.renderFromTraps(this.placedTraps, this.displayMode, options);
    },
    calculateCircleRadius(count) {
      // 画像未読み込み時のフォールバック
      if (this.imageDimensions.minDimension === 0) {
        return 0;
      }

      // 捕獲数0の場合は最小半径
      if (count === 0) {
        return this.minCircleRadius;
      }

      // 捕獲数に応じて対数スケールで半径を計算
      const radiusRange = this.maxCircleRadius - this.minCircleRadius;
      const scaleFactor = radiusRange / Math.log10(this.circleScaleMax + 1);
      const radius = this.minCircleRadius + Math.log10(count + 1) * scaleFactor;

      return Math.min(radius, this.maxCircleRadius);
    },
    handleDrop(event) {
      event.preventDefault();
      const trapNo = event.dataTransfer.getData('trapNo');
      if (!trapNo) return;

      const coordinates = this.getCoordinatesFromEvent(event);
      this.$emit('change', { trapNo, coordinates });
    },
    handleMouseDown(event, trapNo) {
      event.preventDefault();
      this.clearHoverTimer();
      this.hoveredTrap = null;
      this.draggingTrap = trapNo;
    },
    handleMouseMove(event) {
      if (!this.draggingTrap) return;

      const coordinates = this.getCoordinatesFromEvent(event);
      this.$emit('change', { trapNo: this.draggingTrap, coordinates });
    },
    handleMouseUp() {
      this.draggingTrap = null;
    },
    handleTrapMouseEnter(event, trap) {
      // ドラッグ中、ヒートマップモード時はホバーポップアップを表示しない
      if (this.draggingTrap || this.isHeatmapMode) return;

      this.clearHoverTimer();
      this.hoverTimer = setTimeout(() => {
        this.hoveredTrap = trap;
        this.popupPosition = {
          x: event.clientX,
          y: event.clientY,
        };
      }, HOVER_DELAY_MS);
    },
    handleTrapMouseLeave() {
      this.clearHoverTimer();
      this.hoveredTrap = null;
    },
    clearHoverTimer() {
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }
    },
    getCoordinatesFromEvent(event) {
      const img = this.$refs.floorPlanImage;
      const containerRect = img.getBoundingClientRect();
      const imageNaturalSize = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };

      const coordinates = screenToSvgCoordinates(
        event.clientX,
        event.clientY,
        containerRect,
        imageNaturalSize,
      );

      return {
        x: String(coordinates.x),
        y: String(coordinates.y),
      };
    },
    getLabelX(trap) {
      return parseFloat(trap.coordinates.x) + this.markerSize / 2 + 4 * this.sizeScale;
    },
    getTrapName(trap) {
      const name = String(trap.trapNo);
      if (name.length > 5) {
        return `${name.substring(0, 5)}...`;
      }
      return name;
    },
    getCountDisplay(trap) {
      return trap.count != null ? trap.count : 0;
    },
  },
};
</script>

<style scoped>
.floor-plan-container {
  position: relative;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}

.floor-plan-image {
  width: 100%;
  max-width: 100%;
  display: block;
}

.placement-hint-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  /* ヒント表示はクリック不可とし、下のレイヤーにイベントを通過させる */
  pointer-events: none;
}

.placement-hint-card {
  max-width: 400px;
  opacity: 0.95;
}

.heatmap-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;            /* baseレイヤー: Canvas（ヒートマップ） */
  pointer-events: none;   /* マウスイベントをSVGに通過させる */
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;            /* baseレイヤー: SVG（トラップマーカー等） */
  /* SVGオーバーレイ全体でクリックイベントを受け付け、図面上の配置位置を取得可能にする */
  pointer-events: auto;
}

.trap-circle {
  transition: fill 0.3s ease;
  pointer-events: all;
  cursor: move;
}

.trap-group:hover .trap-circle--current {
  fill: rgba(204, 0, 0, 0.8);
}

.trap-marker {
  cursor: move;
  pointer-events: all;
}

.trap-marker--heatmap {
  opacity: 0.5;
}

.trap-group:hover .trap-marker--heatmap {
  opacity: 1;
}

/* 見た目に関わるスタイル（font-family, font-weight等）はインライン属性で指定。
   html-to-imageがCSSを正しく処理できないため、エクスポート画像との一貫性を保つ。 */
.trap-label {
  font-family: sans-serif;
  pointer-events: none;
  user-select: none;
}

.trap-label-name {
  font-weight: bold;
  stroke: white;
  paint-order: stroke fill;
}

.trap-label-count {
  font-weight: normal;
  stroke: white;
  paint-order: stroke fill;
}

.export-info {
  visibility: hidden;
}
</style>
